import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Tab, tabClasses, TabList, Tabs } from "@mui/joy";
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  useMediaQuery,
  Grid,
  Paper,
  Alert,
  LinearProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { generalToastStyle } from "../utils/toast.styles";
import { schemesV2Api } from "../services/schemesV2Api";

function MySchemes() {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(1);
  const [activeSchemes, setActiveSchemes] = useState([]);
  const [oldSchemes, setOldSchemes] = useState([]);
  const [filteredActiveSchemes, setFilteredActiveSchemes] = useState([]);
  const [filteredOldSchemes, setFilteredOldSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [redeemConfirmOpen, setRedeemConfirmOpen] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [redemptionModalOpen, setRedemptionModalOpen] = useState(false);
  const [selectedRedemption, setSelectedRedemption] = useState(null);

  // Check authentication and redirect to login if not authenticated
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");
    
    if (!token || !userId) {
      navigate("/signin?redirect_to=/my-account/my-schemes");
      return;
    }
  }, [navigate]);

  useEffect(() => {
    const fetchUserSchemes = async () => {
      try {
        const userId = localStorage.getItem("user_id");
        if (!userId) {
          setError("User not logged in");
          setLoading(false);
          return;
        }

        console.log("🔐 Fetching schemes for user:", userId);
        console.log("🔐 API URL:", process.env.REACT_APP_API_URL);
        console.log("🔐 Token:", localStorage.getItem('token') ? 'Present' : 'Missing');

        const response = await schemesV2Api.getUserSchemes(userId);
        console.log("🔍 Full API Response:", response);

        if (response?.success) {
          // Get active, completed, and redeemed schemes from the correct response structure
          const activeSchemes = response.data?.activeSchemes || [];
          const completedSchemes = response.data?.completedSchemes || [];
          const redeemedSchemes = response.data?.redeemedSchemes || [];

          console.log("📊 Active schemes:", activeSchemes);
          console.log("📊 Completed schemes:", completedSchemes);
          console.log("📊 Redeemed schemes:", redeemedSchemes);

          // Combine completed and redeemed schemes for the "Old Schemes" tab
          const oldSchemes = [...completedSchemes, ...redeemedSchemes];

          setActiveSchemes(activeSchemes);
          setOldSchemes(oldSchemes);
          
          // Apply scheme filter if provided in URL
          const schemeFilter = searchParams.get('scheme');
          let filteredActive = activeSchemes;
          let filteredOld = oldSchemes;
          
          if (schemeFilter) {
            filteredActive = activeSchemes.filter(scheme => 
              scheme.scheme_id === schemeFilter || 
              scheme.scheme_name?.toLowerCase().includes(schemeFilter.toLowerCase())
            );
            filteredOld = oldSchemes.filter(scheme => 
              scheme.scheme_id === schemeFilter || 
              scheme.scheme_name?.toLowerCase().includes(schemeFilter.toLowerCase())
            );
          }
          
          setFilteredActiveSchemes(filteredActive);
          setFilteredOldSchemes(filteredOld);
          setError("");
        } else {
          console.warn("⚠️ API request failed", response);
          setError("Failed to load schemes");
        }

        setLoading(false);
      } catch (err) {
        console.error("❌ API fetch error:", err);
        console.error("❌ Error details:", err.message);
        console.error("❌ Error response:", err.response?.data);
        
        // Show more specific error message
        const errorMessage = err.response?.data?.message || err.message || "Failed to load schemes";
        setError(`Failed to load schemes: ${errorMessage}`);
        setLoading(false);
      }
    };

    fetchUserSchemes();
  }, [searchParams]);

  const handleRedeem = async () => {
    // For v2 API, redemption functionality might be different
    // For now, just show a message that this feature is not available
    toast.info("Redemption feature will be available soon!", generalToastStyle);
    setRedeemConfirmOpen(false);
  };

  const handleViewRedemption = (enrollment) => {
    setSelectedRedemption(enrollment);
    setRedemptionModalOpen(true);
  };

  const renderSchemeCard = (enrollment, isActive = false) => {
    console.log("enrollment", enrollment);
    const isRedeemed = enrollment.status === 'redeemed';
    const isCompleted = enrollment.status === 'completed';
    
    return (
      <Box key={enrollment.id}>
        {isMobile ? (
          // Mobile Layout
          <Box sx={{ padding: "16px" }}>
            {/* Scheme Name */}
            <Typography
              fontWeight="bold"
              fontSize="1.1rem"
              sx={{ mb: 1, wordBreak: "break-word" }}
            >
              {enrollment.scheme_name || "N/A"}
            </Typography>

            {/* Status Badge */}
            <Box sx={{ mb: 1 }}>
              <Typography
                fontSize="0.7rem"
                sx={{
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  backgroundColor: isRedeemed ? "#4caf50" : isCompleted ? "#2196f3" : "#4caf50",
                  color: "white",
                  fontWeight: "bold",
                  textTransform: "uppercase"
                }}
              >
                {enrollment.status || "Active"}
              </Typography>
            </Box>

            {/* Enrollment ID */}
            <Typography fontSize="0.8rem" color="text.primary" sx={{ mb: 1 }}>
              Enrollment ID: {enrollment.id}
            </Typography>

            {/* Dates */}
            <Typography fontWeight="bold" fontSize="0.9rem" sx={{ mb: 0.5 }}>
              Started On: {enrollment.enrollment_date ? enrollment.enrollment_date.split(" ")[0] : "N/A"}
            </Typography>
            {enrollment.completion_date && (
              <Typography
                fontSize="0.8rem"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Completed: {enrollment.completion_date.split(" ")[0]}
              </Typography>
            )}
            {isRedeemed && enrollment.redemption_details && (
              <Typography
                fontSize="0.8rem"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Redeemed: {enrollment.redemption_details.redemption_date}
              </Typography>
            )}
            <Typography
              fontSize="0.8rem"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Amount: ₹{enrollment.instalment_amount}/month
            </Typography>

            {/* Redemption Details for Redeemed Schemes */}
            {isRedeemed && enrollment.redemption_details && (
              <Box sx={{ mb: 2, p: 1, backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                <Typography fontSize="0.8rem" fontWeight="bold" color="text.primary">
                  Redemption Summary:
                </Typography>
                <Typography fontSize="0.7rem" color="text.secondary">
                  Total Value: ₹{enrollment.redemption_details.total_value?.toLocaleString() || "N/A"}
                </Typography>
                <Typography fontSize="0.7rem" color="text.secondary">
                  Gold Accumulated: {enrollment.redemption_details.gold_accumulated || "0.000000"}g
                </Typography>
              </Box>
            )}

            {/* Buttons */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: "#a36e29",
                  color: "#a36e29",
                  textTransform: "none",
                  borderRadius: "5px",
                  padding: "8px 16px",
                  "&:hover": {
                    borderColor: "#a36e29",
                    backgroundColor: "#f7f7f7",
                  },
                }}
                onClick={() => {
                  // Show redemption modal for redeemed schemes, otherwise navigate to scheme details
                  if (isRedeemed) {
                    handleViewRedemption(enrollment);
                  } else {
                    navigate(`/schemes-v2/${enrollment.scheme_id}`, {
                      state: { enrollment: enrollment }
                    });
                  }
                }}
              >
                {isRedeemed ? "View Redemption" : "View Details"}
              </Button>
            </Box>
          </Box>
        ) : (
          // Desktop Layout
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px",
            }}
          >
            {/* Left Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box>
                <Typography
                  fontWeight="bold"
                  fontSize="1rem"
                  style={{
                    width: "20vw",
                  }}
                >
                  {enrollment.scheme_name || "N/A"}
                </Typography>

                {/* Status Badge */}
                <Box sx={{ mb: 0.5 }}>
                  <Typography
                    fontSize="0.6rem"
                    sx={{
                      display: "inline-block",
                      padding: "2px 6px",
                      borderRadius: "10px",
                      backgroundColor: isRedeemed ? "#4caf50" : isCompleted ? "#2196f3" : "#4caf50",
                      color: "white",
                      fontWeight: "bold",
                      textTransform: "uppercase"
                    }}
                  >
                    {enrollment.status || "Active"}
                  </Typography>
                </Box>

                <Typography fontSize="0.8rem" color="text.primary">
                  Enrollment ID: {enrollment.id}
                </Typography>
              </Box>
            </Box>

            {/* Middle Section */}
            <Box>
              <Typography fontWeight="bold" fontSize="1rem">
                Started On: {enrollment.enrollment_date ? enrollment.enrollment_date.split(" ")[0] : "N/A"}
              </Typography>
              {enrollment.completion_date && (
                <Typography fontSize="0.8rem" color="text.secondary">
                  Completed: {enrollment.completion_date.split(" ")[0]}
                </Typography>
              )}
              {isRedeemed && enrollment.redemption_details && (
                <Typography fontSize="0.8rem" color="text.secondary">
                  Redeemed: {enrollment.redemption_details.redemption_date}
                </Typography>
              )}
              <Typography fontSize="0.8rem" color="text.secondary">
                Amount: ₹{enrollment.instalment_amount}/month
              </Typography>
              {isRedeemed && enrollment.redemption_details && (
                <Typography fontSize="0.7rem" color="text.secondary">
                  Total Value: ₹{enrollment.redemption_details.total_value?.toLocaleString() || "N/A"}
                </Typography>
              )}
            </Box>

            {/* Right Section */}
            <Box>
              <Button
                variant="outlined"
                sx={{
                  borderColor: "#a36e29",
                  color: "#a36e29",
                  textTransform: "none",
                  borderRadius: "5px",
                  padding: "5px 15px",
                  "&:hover": {
                    borderColor: "#a36e29",
                    backgroundColor: "#f7f7f7",
                  },
                }}
                onClick={() => {
                  // Show redemption modal for redeemed schemes, otherwise navigate to scheme details
                  if (isRedeemed) {
                    handleViewRedemption(enrollment);
                  } else {
                    navigate(`/schemes-v2/${enrollment.scheme_id}`, {
                      state: { enrollment: enrollment }
                    });
                  }
                }}
              >
                {isRedeemed ? "View Redemption" : "View Details"}
              </Button>
            </Box>
          </Box>
        )}
        <Divider />
      </Box>
    );
  };

  return (
    <>
              <Box
          sx={{
            width: isMobile ? "95%" : "70%",
            margin: "50px auto 20px auto",
            textAlign: "left",
            px: isMobile ? 2 : 0,
            pb: isMobile ? "80px" : "100px", // Increased bottom padding for desktop to avoid clipping
          }}
        >
        <Typography
          sx={{
            fontFamily: '"Roboto", sans-serif',
            fontSize: isMobile ? "1.5rem" : "1.8rem",
            fontWeight: "bold",
            textAlign: isMobile ? "center" : "left",
            mb: isMobile ? 2 : 0,
          }}
        >
          My Schemes
        </Typography>

        <Tabs
          aria-label="tabs"
          value={selectedTab}
          onChange={(event, newValue) => setSelectedTab(newValue)}
          style={{ marginTop: isMobile ? "16px" : "20px" }}
        >
          <TabList
            sx={{
              p: 0,
              gap: 0,
              borderRadius: "5px",
              width: "max-content",
              border: "1px solid #a7a7a7",
              bgcolor: "#f7f7f7",
              color: "#00000090",
              fontWeight: "bold",
              fontFamily: '"Roboto", sans-serif',
              fontSize: "0.8rem",
              [`& .${tabClasses.root}[aria-selected="true"]`]: {
                boxShadow: "sm",
                border: "2px solid #a36e29",
                bgcolor: "white",
                color: "#a36e29",
                fontWeight: "bold",
              },
              marginTop: isMobile ? "1rem" : "10px",
            }}
          >
            <Tab disableIndicator>Old Schemes</Tab>
            <Tab disableIndicator>Active Schemes</Tab>
          </TabList>

          {loading ? (
            <Box mt={4} textAlign="center">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box mt={4} textAlign="center" color="red">
              {error}
            </Box>
          ) : (
            <>
              {/* Old Schemes */}
              {selectedTab === 0 && (
                <Box
                  mt={4}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                    maxHeight: isMobile ? "60vh" : "55vh", // Increased height for desktop old schemes
                    overflowY: "auto",
                    mb: isMobile ? "0" : "40px", // Add bottom margin for desktop old schemes
                    pb: isMobile ? "0" : "20px", // Add padding bottom for desktop old schemes
                  }}
                >
                  {filteredOldSchemes.length === 0 ? (
                    <Box textAlign="center" p={4} color="text.secondary">
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {searchParams.get('scheme') ? `No old schemes found matching "${searchParams.get('scheme')}"` : 'No old schemes found.'}
                      </Typography>
                      {searchParams.get('scheme') && (
                        <Button
                          variant="contained"
                          onClick={() => navigate(`/schemes-v2?scheme=${searchParams.get('scheme')}`)}
                          sx={{
                            backgroundColor: "#a36e29",
                            "&:hover": {
                              backgroundColor: "#8b5a1f"
                            }
                          }}
                        >
                          View Scheme Details
                        </Button>
                      )}
                    </Box>
                  ) : (
                    filteredOldSchemes.map((scheme) => renderSchemeCard(scheme, false))
                  )}
                </Box>
              )}

              {/* Active Schemes */}
              {selectedTab === 1 && (
                <Box
                  mt={4}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                    maxHeight: isMobile ? "60vh" : "55vh", // Same height as old schemes for consistency
                    overflowY: "auto",
                    mb: isMobile ? "0" : "40px", // Same margin as old schemes for consistency
                    pb: isMobile ? "0" : "20px", // Same padding as old schemes for consistency
                  }}
                >
                  {filteredActiveSchemes.length === 0 ? (
                    <Box textAlign="center" p={4} color="text.secondary">
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {searchParams.get('scheme') ? `No active schemes found matching "${searchParams.get('scheme')}"` : 'No active schemes found.'}
                      </Typography>
                      {searchParams.get('scheme') && (
                        <Button
                          variant="contained"
                          onClick={() => navigate(`/schemes-v2?scheme=${searchParams.get('scheme')}`)}
                          sx={{
                            backgroundColor: "#a36e29",
                            "&:hover": {
                              backgroundColor: "#8b5a1f"
                            }
                          }}
                        >
                          View Scheme Details
                        </Button>
                      )}
                    </Box>
                  ) : (
                    filteredActiveSchemes.map((scheme) =>
                      renderSchemeCard(scheme, true)
                    )
                  )}
                </Box>
              )}
            </>
          )}
        </Tabs>
      </Box>

      <Dialog
        open={redeemConfirmOpen}
        onClose={() => setRedeemConfirmOpen(false)}
      >
        <DialogTitle>Confirm Redemption</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to redeem this scheme?</Typography>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setRedeemConfirmOpen(false)}
              sx={{
                color: "#a36e29",
                borderColor: "#a36e29",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "6px",
                padding: "6px 18px",
                backgroundColor: "#fff",
                "&:hover": {
                  backgroundColor: "#fff",
                  borderColor: "#a36e29",
                },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleRedeem}
              sx={{
                background: "linear-gradient(to right, #a36e29, #e0b872)",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "6px",
                padding: "6px 18px",
                "&:hover": {
                  background: "linear-gradient(to right, #a36e29, #a36e29)",
                },
              }}
            >
              Yes, Redeem
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <ToastContainer />

      <Dialog open={showCode} onClose={() => setShowCode(false)}>
        <DialogTitle>Scheme Code</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              my: 2,
            }}
          >
            <Typography variant="h4" align="center">
              Code not available
            </Typography>
            <Button
              onClick={() => {
                navigator.clipboard.writeText("Code not available");
                toast.success("Code copied to clipboard!", generalToastStyle);
              }}
              sx={{
                minWidth: "auto",
                p: 1,
                "&:hover": {
                  backgroundColor: "rgba(163, 110, 41, 0.1)",
                },
              }}
            >
              <ContentCopyIcon sx={{ color: "#a36e29" }} />
            </Button>
          </Box>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() => setShowCode(false)}
              sx={{
                background: "linear-gradient(to right, #a36e29, #e0b872)",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "6px",
                padding: "6px 18px",
                "&:hover": {
                  background: "linear-gradient(to right, #a36e29, #a36e29)",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Redemption Details Modal */}
      <Dialog 
        open={redemptionModalOpen} 
        onClose={() => setRedemptionModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
            <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 30 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#4caf50" }}>
              Redemption Details
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#A36E29" }}>
            {selectedRedemption?.scheme_name || "Scheme Details"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {selectedRedemption && (
            <Box>
              {/* Success Message */}
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3,
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                  borderColor: "rgba(76, 175, 80, 0.3)",
                  '& .MuiAlert-icon': {
                    color: "#4caf50",
                  },
                  '& .MuiAlert-message': {
                    color: "#4caf50",
                  }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Successfully Redeemed
                </Typography>
                <Typography variant="body2">
                  Your scheme has been successfully redeemed and the benefits have been applied.
                </Typography>
              </Alert>

              {/* Redemption Summary */}
              {selectedRedemption.redemption_details && (
                <Paper sx={{ p: 3, mb: 3, backgroundColor: "rgba(76, 175, 80, 0.1)", border: "1px solid rgba(76, 175, 80, 0.3)" }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#4caf50" }}>
                    Redemption Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Redemption Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedRedemption.redemption_details.redemption_date || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Investment
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ₹{parseFloat(selectedRedemption.redemption_details.total_investment || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Value
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#4caf50" }}>
                        ₹{parseFloat(selectedRedemption.redemption_details.total_value || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Gold Accumulated
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {parseFloat(selectedRedemption.redemption_details.gold_accumulated || 0).toFixed(4)} grams
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Bonus Earned
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ₹{parseFloat(selectedRedemption.redemption_details.bonus_earned || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Scheme Details */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Enrollment Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedRedemption.enrollment_date ? new Date(selectedRedemption.enrollment_date).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Installment Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ₹{parseFloat(selectedRedemption.instalment_amount || 0).toLocaleString()}
                    {selectedRedemption.scheme_type === 'daily' ? '/day' : '/month'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Invested
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ₹{parseFloat(selectedRedemption.plan_progress?.total_paid || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Gold Grams Earned
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {parseFloat(selectedRedemption.gold_grams_accumulated || 0).toFixed(4)} grams
                  </Typography>
                </Grid>
              </Grid>

              {/* Progress */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Scheme Progress
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Completion
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#4caf50" }}>
                    {selectedRedemption.plan_progress?.percentage || 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={selectedRedemption.plan_progress?.percentage || 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#f5f5f5",
                    "& .MuiLinearProgress-bar": {
                      background: "linear-gradient(to right, #4caf50, #66bb6a)"
                    }
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedRedemption.plan_progress?.periods_paid || 0} of {selectedRedemption.plan_progress?.total_periods || 0} installments completed
                </Typography>
              </Box>

              {/* Completion Reward */}
              <Paper sx={{ p: 3, backgroundColor: "rgba(163, 110, 41, 0.1)", border: "1px solid rgba(163, 110, 41, 0.3)" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#A36E29" }}>
                  Completion Reward
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#A36E29" }}>
                  {selectedRedemption.completion_reward || 'N/A'}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={() => setRedemptionModalOpen(false)}
            sx={{
              background: "linear-gradient(to right, #a36e29, #e0b872)",
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "6px",
              padding: "6px 18px",
              "&:hover": {
                background: "linear-gradient(to right, #a36e29, #a36e29)",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Bottom Navigation - Only show on mobile */}
      {isMobile && (
        <BottomNavigation
          showLabels
          style={{
            background: "rgba(163,110,41)",
            border: "1px solid #a36e29",
            borderRadius: "50px",
            height: "40px",
            width: "95%",
            position: "fixed",
            bottom: 20,
            zIndex: 1000,
            left: "50%",
            transform: "translateX(-50%)",
          }}
          sx={{
            "& .MuiBottomNavigationAction-root": {
              minWidth: "auto",
              padding: "6px 4px",
            },
          }}
        >
          <BottomNavigationAction
            label="Profile"
            sx={{
              "& .MuiBottomNavigationAction-label": {
                fontFamily: '"Roboto", sans-serif',
                fontSize: "0.8rem",
              },
            }}
            onClick={() => navigate("/my-account")}
          />
          <BottomNavigationAction
            label="Orders"
            sx={{
              "& .MuiBottomNavigationAction-label": {
                fontFamily: '"Roboto", sans-serif',
                fontSize: "0.8rem",
              },
            }}
            onClick={() => navigate("/my-account/orders")}
          />
          <BottomNavigationAction
            label="Address"
            sx={{
              "& .MuiBottomNavigationAction-label": {
                fontFamily: '"Roboto", sans-serif',
                fontSize: "0.8rem",
              },
            }}
            onClick={() => navigate("/my-account/address")}
          />
          <BottomNavigationAction
            label="Wallet"
            sx={{
              "& .MuiBottomNavigationAction-label": {
                fontFamily: '"Roboto", sans-serif',
                fontSize: "0.8rem",
              },
            }}
            onClick={() => navigate("/my-account/wallet")}
          />
          <BottomNavigationAction
            label="Schemes"
            sx={{
              "& .MuiBottomNavigationAction-label": {
                fontFamily: '"Roboto", sans-serif',
                fontSize: "0.8rem",
                fontWeight: "600",
                color: "white",
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate("/my-account/my-schemes")}
          />
        </BottomNavigation>
      )}
    </>
  );
}

export default MySchemes;
